import React, { useEffect, useState } from "react";

import {
  AppointmentSlotsByDate,
  BANNER_VARIANTS,
  BannerVariant,
  BOOKING_FLOW_EVENTS,
  Provider,
  useBookingFlowEvents,
  VISIT_TYPES,
  VisitType,
} from "~/services";
import { NIRVANA_STATUS, NirvanaStatus } from "~/services/nirvana/types";
import { Button } from "~/shared";
import { TimeSlot } from "~/shared.types";
import { useIsPatientRole } from "~/stores";
import { useNirvanaStore } from "~/stores/use-nirvana-store";
import { ErrorText } from "~/ui/common";
import {
  CalendarHandlers,
  CalendarState,
  SelectionHandlers,
  SelectionState,
} from "../utils";
import { CoverageStatusBanner } from "./CoverageStatusBanner";
import {
  CalendarHeader,
  CalendarPopup,
  Copyright,
  DayNavigator,
  DaySelector,
  FirstAvailableScheduleSection,
  Header,
  LeftPanel,
  ProviderSchedulesSection,
} from "./index";

const STATUS_TO_BANNER_VARIANT: Record<NirvanaStatus, BannerVariant> = {
  [NIRVANA_STATUS.COVERED]: BANNER_VARIANTS.VISIT_COVERED,
  [NIRVANA_STATUS.NOT_COVERED]: BANNER_VARIANTS.VISIT_NOT_COVERED,
  [NIRVANA_STATUS.INCONCLUSIVE]: BANNER_VARIANTS.PLAN_NOT_RETURNED,
  [NIRVANA_STATUS.SKIPPED]: BANNER_VARIANTS.NO_BANNER,
};

const TIME_SLOT_ERROR_MESSAGE = "Please select a time before continuing.";
const TIME_SLOT_ERROR_DESKTOP_ID = "time-slot-error-desktop";
const TIME_SLOT_ERROR_MOBILE_ID = "time-slot-error-mobile";

export type ScheduleVisitProps = {
  visitType: VisitType;
  isProviderView?: boolean;
  handleGoBack: () => void;
  calendar: CalendarState;
  selection: SelectionState;
  calendarHandlers: CalendarHandlers;
  selectionHandlers: SelectionHandlers;
  appointmentSlotsByProvider: Provider[];
  appointmentSlotsByFirstAvailable?: AppointmentSlotsByDate;
  isPendingProviderView: boolean;
  isPendingFirstAvailableView?: boolean;
  onContinue: () => void;
};

export const ScheduleVisit = ({
  visitType,
  isProviderView = true,
  handleGoBack,
  calendar,
  selection,
  calendarHandlers,
  selectionHandlers,
  appointmentSlotsByProvider,
  appointmentSlotsByFirstAvailable,
  isPendingProviderView,
  isPendingFirstAvailableView,
  onContinue,
}: ScheduleVisitProps) => {
  const nirvanaResult = useNirvanaStore((state) => state.result);
  const isInitialVisit = visitType === VISIT_TYPES.INITIAL;
  const { trackEvent } = useBookingFlowEvents();

  useEffect(() => {
    if (!isInitialVisit || !nirvanaResult) return;

    trackEvent(
      BOOKING_FLOW_EVENTS.BANNER_VARIANT,
      STATUS_TO_BANNER_VARIANT[nirvanaResult.status],
    );
  }, [isInitialVisit, nirvanaResult]);

  const {
    selectedDay,
    calendarDays,
    monthYear,
    maxAllowedDate,
    isCalendarOpen,
    hasAppointments,
    firstAvailableDay,
  } = calendar;

  const { selectedTimeSlot, selectedProvider } = selection;

  const {
    onDaySelect,
    onMonthChange,
    onOpenCalendar,
    onCloseCalendar,
    onReturnToFirstAvailable,
  } = calendarHandlers;

  const { onTimeSlotSelect } = selectionHandlers;
  const isPatient = useIsPatientRole();

  const [hasTriedToContinue, setHasTriedToContinue] = useState(false);
  const showTimeSlotError = hasTriedToContinue && !selectedTimeSlot;

  const handleTimeSlotSelect = (timeSlot: TimeSlot, providerId?: number) => {
    setHasTriedToContinue(false);
    onTimeSlotSelect(timeSlot, providerId);
  };

  const handleContinueClick = () => {
    if (!selectedTimeSlot) {
      setHasTriedToContinue(true);
      return;
    }
    onContinue();
  };
  return (
    <div className="flex min-h-screen flex-col">
      {isInitialVisit && (
        <div className="md:hidden">
          <CoverageStatusBanner result={nirvanaResult} />
        </div>
      )}

      <Header visitType={visitType} handleGoBack={handleGoBack} />

      <div className="flex w-full justify-center py-6">
        <div className="flex w-full flex-col md:w-auto">
          {isInitialVisit && (
            <div className="mb-5 hidden md:block">
              <CoverageStatusBanner result={nirvanaResult} />
            </div>
          )}

          <div className="flex w-full flex-col justify-center gap-x-8 px-4 md:flex-row md:px-0">
            <div className="hidden md:block">
              <LeftPanel visitType={visitType} handleGoBack={handleGoBack} />
            </div>

            <div className="flex w-full justify-center md:w-[420px]">
              <div className="w-full">
                {(visitType !== VISIT_TYPES.INITIAL || !isPatient) && (
                  <CalendarHeader
                    calendarDays={calendarDays}
                    onDaySelect={onDaySelect}
                    selectedDay={selectedDay}
                    onMonthChange={onMonthChange}
                    onOpen={onOpenCalendar}
                  />
                )}
                <div className="flex flex-col gap-3 pb-24 md:pb-0">
                  {visitType === VISIT_TYPES.INITIAL && isPatient ? (
                    <DayNavigator
                      days={calendarDays}
                      selectedDay={selectedDay}
                      onDaySelect={onDaySelect}
                      hasAppointments={hasAppointments}
                      onMonthChange={onMonthChange}
                      currentMonthYear={monthYear}
                      maxAllowedDate={maxAllowedDate}
                      firstAvailableDay={firstAvailableDay}
                      onReturnToFirstAvailable={onReturnToFirstAvailable}
                    />
                  ) : (
                    <DaySelector
                      days={calendarDays}
                      selectedDay={selectedDay}
                      onDaySelect={onDaySelect}
                      hasAppointments={hasAppointments}
                      onMonthChange={onMonthChange}
                      currentMonthYear={monthYear}
                      maxAllowedDate={maxAllowedDate}
                      hideSelectedDay={
                        !isPendingProviderView &&
                        appointmentSlotsByProvider.length === 0
                      }
                    />
                  )}
                  {isProviderView ? (
                    <ProviderSchedulesSection
                      appointmentSlotsByProvider={appointmentSlotsByProvider}
                      selectedDate={selectedDay.date}
                      selectedTimeSlot={selectedTimeSlot}
                      selectedProvider={selectedProvider}
                      onTimeSlotSelect={handleTimeSlotSelect}
                      isPending={isPendingProviderView}
                    />
                  ) : (
                    <FirstAvailableScheduleSection
                      appointmentSlotsByFirstAvailable={
                        appointmentSlotsByFirstAvailable
                      }
                      selectedDate={selectedDay.date}
                      selectedTimeSlot={selectedTimeSlot}
                      onTimeSlotSelect={handleTimeSlotSelect}
                      isPending={isPendingFirstAvailableView}
                    />
                  )}
                  <Button
                    aria-disabled={!selectedTimeSlot}
                    aria-describedby={
                      showTimeSlotError ? TIME_SLOT_ERROR_DESKTOP_ID : undefined
                    }
                    onClick={handleContinueClick}
                    className="mt-5 hidden w-full md:block"
                    variant="default"
                  >
                    Continue
                  </Button>
                  {showTimeSlotError && (
                    <ErrorText
                      id={TIME_SLOT_ERROR_DESKTOP_ID}
                      className="hidden md:block"
                    >
                      {TIME_SLOT_ERROR_MESSAGE}
                    </ErrorText>
                  )}
                </div>
              </div>
              <Copyright className="hidden md:absolute md:bottom-4 md:right-4" />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 right-0 w-full p-2 md:hidden">
        {showTimeSlotError && (
          <ErrorText id={TIME_SLOT_ERROR_MOBILE_ID} className="mb-2">
            {TIME_SLOT_ERROR_MESSAGE}
          </ErrorText>
        )}
        <Button
          className="w-full"
          aria-disabled={!selectedTimeSlot}
          aria-describedby={
            showTimeSlotError ? TIME_SLOT_ERROR_MOBILE_ID : undefined
          }
          onClick={handleContinueClick}
          size="sm"
        >
          Continue
        </Button>
      </div>

      {isCalendarOpen && (
        <CalendarPopup
          onClose={onCloseCalendar}
          onDaySelect={onDaySelect}
          calendarDays={calendarDays}
          isSelectable={hasAppointments}
          selectedDay={selectedDay}
          onMonthChange={onMonthChange}
          initialDate={selectedDay.date}
          isPending={isPendingProviderView}
        />
      )}
    </div>
  );
};
